import { gql, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import type { EntityOrganizationUser } from '@interfaces/user';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { NumberField } from '@ovComponents/3-pattern/numberField/numberField';
import { OrganizationUserSelect } from '@ovComponents/3-pattern/organizationUserSelect/organizationUserSelect';
import { SwitchField } from '@ovComponents/3-pattern/switchField/switchField';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { UserContext } from '@providers/userContextProvider';
import {
  Box,
  CircularProgress,
  Grid,
  Tooltip,
  Typography,
  useTheme,
} from 'hephaestus/web';
import { Lock } from 'hephaestus/webIcons';
import { uniq } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import type { FormSignerMapping } from './formSignerMapping';

type ManageSignersSubStepOptions = {
  subtitle?: TranslatedString;
  editSigningOrder?: boolean;
  autoAssignCurrentUser?: boolean;
};

type SignerUser = {
  firstName?: string;
  lastName?: string;
  email?: string;
  id?: string;
};

const FETCH_SIGNER_TYPES = gql`
  query fetchSignerTypes($input: FetchSignerTypesInput!) {
    fetchSignerTypes(input: $input) {
      signerTypes {
        id
        key
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        isPrimary
        defaultSigningOrder
        roleProfiles {
          id
        }
      }
      totalCount
    }
  }
`;

const FETCH_USER = gql`
  query fetchUser($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        type
        accessibleBy(input: { excludeRepCodeUsers: true }) {
          relation
          organizationUser {
            id
            firstName
            lastName
            email
          }
        }
      }
    }
  }
`;

const FETCH_FORM_COMPLETIONS = gql`
  query fetchFormCompletions($input: FetchFormCompletionsInput!) {
    fetchFormCompletions(input: $input) {
      formCompletions {
        id
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        state
        signingType
        requiredSignatures {
          signerKey
        }
      }
      totalCount
    }
  }
`;

const FETCH_ROLES = gql`
  query fetchRoles($input: FetchRolesInput!) {
    fetchRoles(input: $input) {
      roles {
        id
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        roleProfile {
          id
        }
      }
    }
  }
`;

type RoleWithProfile = {
  id: string;
  translatedName: TranslatedString;
  roleProfile?: {
    id: string;
  };
};

export const ManageSigners = ({
  onNext,
  workflowCompletion,
  options,
}: {
  onNext: () => void;
  workflowCompletion: any;
  options: ManageSignersSubStepOptions;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['workflowCompletions']);
  const theme = useTheme();
  const { activeOrganization, userContext } = useContext(UserContext);
  const [signerMapping, setSignerMapping] = useState<any[]>([]);
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const [signerMappingLoading, setSignerMappingLoading] = useState(true);

  const { data: formCompletionsData } = useQuery(FETCH_FORM_COMPLETIONS, {
    variables: {
      input: {
        filter: {
          workflowCompletionId: workflowCompletion.id,
          states: ['UNSIGNED', 'SIGNED'],
        },
        pagination: {
          perPage: 100,
        },
      },
    },
    skip: !workflowCompletion?.id,
  });

  const { data } = useQuery(FETCH_SIGNER_TYPES, {
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization.id,
        },
        pagination: { perPage: 1000 },
      },
    },
  });

  const { data: userData } = useQuery(FETCH_USER, {
    fetchPolicy: 'no-cache',
    variables: {
      userId: workflowCompletion.objectId,
    },
  });

  const hasRestrictedSigners = signerMapping.some(
    (s: FormSignerMapping) => s.roleProfiles?.length,
  );

  const { data: rolesData } = useQuery(FETCH_ROLES, {
    variables: {
      input: {
        filter: { organizationId: activeOrganization.id },
        pagination: { perPage: 1000 },
      },
    },
    skip: !hasRestrictedSigners,
  });

  const roles: RoleWithProfile[] = rolesData?.fetchRoles?.roles ?? [];

  const getRestrictedRoleNames = (signer: FormSignerMapping): string[] => {
    if (!signer.roleProfiles?.length) return [];
    const profileIds = new Set(signer.roleProfiles.map((p) => p.id));

    return roles
      .filter(
        (role) => role.roleProfile?.id && profileIds.has(role.roleProfile.id),
      )
      .map((role) => translateBackendString(role.translatedName))
      .filter(Boolean);
  };

  useEffect(() => {
    if (
      formCompletionsData?.fetchFormCompletions?.formCompletions &&
      data?.fetchSignerTypes?.signerTypes &&
      userData?.fetchUser?.user
    ) {
      let signerKeys: string[] = [];
      formCompletionsData.fetchFormCompletions.formCompletions.forEach(
        (formCompletion: any) => {
          signerKeys.push(
            ...formCompletion.requiredSignatures.map(
              (signature: any) => signature.signerKey,
            ),
          );
        },
      );
      signerKeys = uniq(signerKeys);
      const currentSignerMapping =
        workflowCompletion.context.signerMapping || [];

      const soleSignerType =
        signerKeys.length === 1
          ? data.fetchSignerTypes.signerTypes.find(
              (type: { key: string }) => type.key === signerKeys[0],
            )
          : undefined;

      // Auto-assign only for a single unrestricted signer; role-profile
      // restrictions must stay on this step for manual selection.
      const autoAssignCurrentUser =
        options.autoAssignCurrentUser &&
        !!soleSignerType &&
        !soleSignerType.roleProfiles?.length;

      let defaultSignerUser: SignerUser | undefined;
      if (autoAssignCurrentUser) {
        const signerUser =
          userData.fetchUser.user.type !== 'INDIVIDUAL'
            ? userData.fetchUser.user.accessibleBy.find(
                (item: EntityOrganizationUser) => !!item.organizationUser,
              )?.organizationUser
            : undefined;

        defaultSignerUser = {
          id: signerUser ? signerUser.id : userContext.id,
          firstName: signerUser ? signerUser.firstName : userContext.firstName,
          lastName: signerUser ? signerUser.lastName : userContext.lastName,
          email: signerUser ? signerUser.email : userContext.email,
        };
      }
      const formSigners = signerKeys
        .map((signerKey: string) => {
          let signer = currentSignerMapping.find(
            (s: any) => s.signerKey === signerKey,
          );
          const signerType = data.fetchSignerTypes.signerTypes.find(
            (type: any) => type.key === signerKey,
          );
          if (!signerType) {
            return undefined;
          }

          if (!signer && defaultSignerUser) {
            signer = {
              signerName: signerType.translatedName,
              firstName: defaultSignerUser.firstName,
              lastName: defaultSignerUser.lastName,
              email: defaultSignerUser.email,
              organizationUserId: defaultSignerUser.id,
            };
          }
          return {
            signerKey,
            signerName: signerType.translatedName,
            firstName: signer?.firstName,
            lastName: signer?.lastName,
            email: signer?.email,
            organizationUserId: signer?.organizationUserId,
            useOrgUser: true,
            signingOrder: signerType?.defaultSigningOrder,
            roleProfiles: signerType?.roleProfiles,
          };
        })
        .filter((s) => s !== undefined) as FormSignerMapping[];
      setSignerMapping(formSigners);

      if (autoAssignCurrentUser && formSigners.length === 1) {
        save(formSigners);
      } else {
        setSignerMappingLoading(false);
      }
    }
  }, [workflowCompletion, data, userData, formCompletionsData]);

  const save = async (formSigners?: FormSignerMapping[]) => {
    await setWorkflowData({
      ...workflowData,
      signerMapping: formSigners ?? signerMapping,
    });
    await onNext();
  };

  if (signerMappingLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          minHeight: '300px',
        }}
      >
        <Typography variant='headingMedium'>
          {t('manageSigners.manageSigners')}
        </Typography>
        <Typography variant='bodyMedium' sx={{ mb: 4, fontWeight: 500 }}>
          {t('manageSigners.description')}
        </Typography>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <WorkflowCompletionForm
      onSubmit={() => save()}
      submitButtonProps={{
        label: t('continue'),
        dataTestId: 'manage-signers-continue-button',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant='displayLarge' sx={{ mt: 1, mb: 2 }}>
          {t('manageSigners.manageSigners')}
        </Typography>
        {options?.subtitle && (
          <Typography variant='bodyLarge' sx={{ mb: 3 }}>
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackendString(options?.subtitle)}
            </ReactMarkdown>
          </Typography>
        )}
        {signerMapping.map((signer: FormSignerMapping) => {
          const restrictedRoleNames = getRestrictedRoleNames(signer);

          return (
            <Box
              key={signer.signerKey}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
              }}
            >
              <Card variant='outlined' sx={{ width: '100%' }}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography variant='bodyLarge'>
                      {translateBackendString(signer.signerName)}
                    </Typography>
                    {signer.roleProfiles?.length ? (
                      <Tooltip
                        title={
                          rolesData
                            ? `${t('manageSigners.restrictedToRoles')}: ${restrictedRoleNames.join(', ')}`
                            : undefined
                        }
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: theme.color.onSurfaceVariant,
                            cursor: 'help',
                          }}
                        >
                          <Lock fontSize='small' />
                          <Typography
                            variant='bodyLarge'
                            sx={{
                              color: theme.color.onSurfaceVariant,
                              textDecoration: 'underline dotted',
                              textUnderlineOffset: '3px',
                            }}
                          >
                            {t('manageSigners.restrictedByRole')}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      <SwitchField
                        checked={signer.useOrgUser}
                        label={t('manageSigners.useOrgUser')}
                        onChange={(e: any) =>
                          setSignerMapping(
                            signerMapping.map((s: FormSignerMapping) =>
                              s.signerKey === signer.signerKey
                                ? { ...s, useOrgUser: e }
                                : s,
                            ),
                          )
                        }
                      />
                    )}
                  </Box>
                  {signer.useOrgUser ? (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid
                        size={{ xs: 12, sm: options.editSigningOrder ? 9 : 12 }}
                      >
                        <OrganizationUserSelect
                          label={t('manageSigners.user')}
                          value={signer.organizationUserId ?? ''}
                          filter={{
                            roleProfileIds: signer.roleProfiles?.length
                              ? signer.roleProfiles.map((r) => r.id)
                              : undefined,
                          }}
                          onChange={(value: any) =>
                            setSignerMapping(
                              signerMapping.map((s: FormSignerMapping) =>
                                s.signerKey === signer.signerKey
                                  ? {
                                      ...s,
                                      organizationUserId: value?.id,
                                      firstName: value?.firstName,
                                      lastName: value?.lastName,
                                      email: value?.email,
                                    }
                                  : s,
                              ),
                            )
                          }
                        />
                      </Grid>
                      {options.editSigningOrder && (
                        <Grid size={{ xs: 12, sm: 3 }}>
                          <NumberField
                            sx={{ width: '100px' }}
                            label={t('manageSigners.signingOrder')}
                            number={signer.signingOrder?.toString() ?? ''}
                            setNumber={(value: string) =>
                              setSignerMapping(
                                signerMapping.map((s: FormSignerMapping) =>
                                  s.signerKey === signer.signerKey
                                    ? {
                                        ...s,
                                        signingOrder: parseInt(value, 10),
                                      }
                                    : s,
                                ),
                              )
                            }
                          />
                        </Grid>
                      )}
                    </Grid>
                  ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          label={t('manageSigners.firstName')}
                          value={signer.firstName}
                          onChange={(e: any) =>
                            setSignerMapping(
                              signerMapping.map((s: FormSignerMapping) =>
                                s.signerKey === signer.signerKey
                                  ? { ...s, firstName: e.target.value }
                                  : s,
                              ),
                            )
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          label={t('manageSigners.lastName')}
                          value={signer.lastName}
                          onChange={(e: any) =>
                            setSignerMapping(
                              signerMapping.map((s: FormSignerMapping) =>
                                s.signerKey === signer.signerKey
                                  ? { ...s, lastName: e.target.value }
                                  : s,
                              ),
                            )
                          }
                        />
                      </Grid>
                      <Grid
                        size={{ xs: 12, sm: options.editSigningOrder ? 9 : 12 }}
                      >
                        <TextField
                          fullWidth
                          label={t('manageSigners.email')}
                          value={signer.email}
                          onChange={(e: any) =>
                            setSignerMapping(
                              signerMapping.map((s: FormSignerMapping) =>
                                s.signerKey === signer.signerKey
                                  ? { ...s, email: e.target.value }
                                  : s,
                              ),
                            )
                          }
                        />
                      </Grid>
                      {options.editSigningOrder && (
                        <Grid size={{ xs: 12, sm: 3 }}>
                          <NumberField
                            label={t('manageSigners.signingOrder')}
                            number={signer.signingOrder?.toString() ?? ''}
                            setNumber={(value: string) =>
                              setSignerMapping(
                                signerMapping.map((s: FormSignerMapping) =>
                                  s.signerKey === signer.signerKey
                                    ? {
                                        ...s,
                                        signingOrder: parseInt(value, 10),
                                      }
                                    : s,
                                ),
                              )
                            }
                          />
                        </Grid>
                      )}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>
    </WorkflowCompletionForm>
  );
};
